if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();

        this.mql = window.matchMedia(
          '(min-width: 990px)'
        );

        this.mqlTablet = window.matchMedia(
          '(min-width: 750px)'
        );

        this.infoButtonDesktop =
          this.querySelector(
            '.quantity-popover__info-button--icon-only'
          );

        this.infoButtonMobile =
          this.querySelector(
            '.quantity-popover__info-button--icon-with-label'
          );

        this.popoverInfo =
          this.querySelector(
            '.quantity-popover__info'
          );

        this.closeButton =
          this.querySelector(
            '.button-close'
          );

        this.boundClosePopover =
          this.closePopover.bind(this);

        this.boundTogglePopover =
          this.togglePopover.bind(this);

        this.boundDesktopMouseEnter =
          this.onDesktopMouseEnter.bind(this);

        this.boundDesktopMouseLeave =
          this.onDesktopMouseLeave.bind(this);

        this.boundKeydown =
          this.onKeydown.bind(this);

        this.boundFocusOut =
          this.onFocusOut.bind(this);
      }

      connectedCallback() {
        this.bindEvents();

        /*
         * Ensure ARIA state matches
         * the initial hidden state.
         */
        this.syncInitialState();
      }

      disconnectedCallback() {
        this.unbindEvents();
      }

      bindEvents() {
        if (this.closeButton) {
          this.closeButton.addEventListener(
            'click',
            this.boundClosePopover
          );
        }

        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener(
            'click',
            this.boundTogglePopover
          );

          this.infoButtonDesktop.addEventListener(
            'mouseenter',
            this.boundDesktopMouseEnter
          );

          this.infoButtonDesktop.addEventListener(
            'mouseleave',
            this.boundDesktopMouseLeave
          );
        }

        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener(
            'click',
            this.boundTogglePopover
          );
        }

        /*
         * One Escape listener only.
         *
         * Prevents accumulating keyup listeners
         * every time the popover opens.
         */
        this.addEventListener(
          'keydown',
          this.boundKeydown
        );

        /*
         * Close when keyboard focus leaves the
         * entire quantity-popover component.
         */
        this.addEventListener(
          'focusout',
          this.boundFocusOut
        );

        if (this.popoverInfo) {
          this.popoverInfo.addEventListener(
            'mouseleave',
            this.boundDesktopMouseLeave
          );
        }
      }

      unbindEvents() {
        this.closeButton?.removeEventListener(
          'click',
          this.boundClosePopover
        );

        this.infoButtonDesktop?.removeEventListener(
          'click',
          this.boundTogglePopover
        );

        this.infoButtonDesktop?.removeEventListener(
          'mouseenter',
          this.boundDesktopMouseEnter
        );

        this.infoButtonDesktop?.removeEventListener(
          'mouseleave',
          this.boundDesktopMouseLeave
        );

        this.infoButtonMobile?.removeEventListener(
          'click',
          this.boundTogglePopover
        );

        this.removeEventListener(
          'keydown',
          this.boundKeydown
        );

        this.removeEventListener(
          'focusout',
          this.boundFocusOut
        );

        this.popoverInfo?.removeEventListener(
          'mouseleave',
          this.boundDesktopMouseLeave
        );
      }

      syncInitialState() {
        if (!this.popoverInfo) {
          return;
        }

        const isHidden =
          this.popoverInfo.hasAttribute(
            'hidden'
          );

        this.infoButtonDesktop?.setAttribute(
          'aria-expanded',
          String(!isHidden)
        );

        this.infoButtonMobile?.setAttribute(
          'aria-expanded',
          String(!isHidden)
        );
      }

      getActiveButton() {
        /*
         * Prefer whichever button is actually visible.
         *
         * This is safer than relying exclusively on
         * viewport width because theme CSS can change
         * which control is displayed.
         */
        const desktopVisible =
          this.infoButtonDesktop &&
          this.infoButtonDesktop.offsetParent !==
            null;

        const mobileVisible =
          this.infoButtonMobile &&
          this.infoButtonMobile.offsetParent !==
            null;

        if (desktopVisible) {
          return this.infoButtonDesktop;
        }

        if (mobileVisible) {
          return this.infoButtonMobile;
        }

        /*
         * Fallback for transitions / hidden sections.
         */
        if (
          this.mql.matches &&
          this.infoButtonDesktop
        ) {
          return this.infoButtonDesktop;
        }

        return (
          this.infoButtonMobile ||
          this.infoButtonDesktop ||
          null
        );
      }

      togglePopover(event) {
        event?.preventDefault?.();

        if (!this.popoverInfo) {
          return;
        }

        const button =
          event?.currentTarget ===
            this.infoButtonDesktop ||
          event?.currentTarget ===
            this.infoButtonMobile
            ? event.currentTarget
            : this.getActiveButton();

        if (!button) {
          return;
        }

        const isExpanded =
          button.getAttribute(
            'aria-expanded'
          ) === 'true';

        if (isExpanded) {
          this.closePopover(event);
          return;
        }

        this.openPopover(button);
      }

      openPopover(button) {
        if (
          !button ||
          !this.popoverInfo
        ) {
          return;
        }

        /*
         * Keep both responsive controls
         * synchronized.
         */
        this.infoButtonDesktop?.setAttribute(
          'aria-expanded',
          'false'
        );

        this.infoButtonMobile?.setAttribute(
          'aria-expanded',
          'false'
        );

        button.setAttribute(
          'aria-expanded',
          'true'
        );

        this.popoverInfo.removeAttribute(
          'hidden'
        );

        button.classList.add(
          'quantity-popover__info-button--open'
        );

        this.infoButtonDesktop?.classList.add(
          'quantity-popover__info-button--icon-only--animation'
        );
      }

      closePopover(event) {
        event?.preventDefault?.();

        if (!this.popoverInfo) {
          return;
        }

        /*
         * Do not close when the pointer or keyboard
         * focus is simply moving between elements
         * inside this quantity popover.
         */
        const relatedTarget =
          event?.relatedTarget;

        if (
          relatedTarget instanceof Node &&
          this.contains(relatedTarget)
        ) {
          return;
        }

        this.infoButtonDesktop?.setAttribute(
          'aria-expanded',
          'false'
        );

        this.infoButtonMobile?.setAttribute(
          'aria-expanded',
          'false'
        );

        this.infoButtonDesktop?.classList.remove(
          'quantity-popover__info-button--open'
        );

        this.infoButtonMobile?.classList.remove(
          'quantity-popover__info-button--open'
        );

        this.infoButtonDesktop?.classList.remove(
          'quantity-popover__info-button--icon-only--animation'
        );

        this.popoverInfo.setAttribute(
          'hidden',
          ''
        );
      }

      onDesktopMouseEnter(event) {
        /*
         * Preserve hover behaviour only on
         * tablet/desktop-sized layouts.
         */
        if (!this.mqlTablet.matches) {
          return;
        }

        if (!this.popoverInfo) {
          return;
        }

        this.openPopover(
          event.currentTarget
        );
      }

      onDesktopMouseLeave(event) {
        if (!this.mqlTablet.matches) {
          return;
        }

        this.closePopover(event);
      }

      onFocusOut(event) {
        const nextElement =
          event.relatedTarget;

        if (
          nextElement instanceof Node &&
          this.contains(nextElement)
        ) {
          return;
        }

        this.closePopover(event);
      }

      onKeydown(event) {
        if (event.key !== 'Escape') {
          return;
        }

        const button =
          this.getActiveButton();

        const isOpen =
          this.popoverInfo &&
          !this.popoverInfo.hasAttribute(
            'hidden'
          );

        if (!isOpen) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.closePopover();

        button?.focus();
      }
    }
  );
}